/**
 * Packet progress — the whole URL contract for a multi-form packet link.
 *
 * WHY THIS FILE EXISTS
 *   Harrisburg Smiles sends a "New Patient Packet" of 8 forms as ONE short link. A patient filled
 *   section 1, saw a button that said **Submit**, and stopped; the office received 1 of 8. Reopening
 *   the link always restarted at form 1, so resuming meant re-doing finished sections and filing
 *   duplicate PDFs into Open Dental imaging.
 *
 *   Everything the renderer needs to say "New Patient Packet · Section 3 of 8" and to decide between
 *   "Save & Continue" and "Submit" travels in the ROUTE QUERY, because the hops are real navigations
 *   (UrlRedirect → VerifyForm → ViewForm → ViewForm …) and a Vue-router PARAM does not survive a page
 *   refresh. This file is the single place that writes those keys and the single place that reads
 *   them back, so the two can never disagree.
 *
 * THE KEYS (all strings — they are URL query values)
 *   packetNext     comma list of the urlIDs still to come, in order          (pre-existing)
 *   packetVerified '0'/'1' — pre-existing, nothing reads it; untouched here  (pre-existing)
 *   packetTotal    the FULL packet size, forwarded verbatim on every hop     (new)
 *   packetStep     1-based position of THIS form in the full packet          (new)
 *   packetSteps    comma list of positions, parallel to packetNext           (new)
 *   packetName     the packet's name                                          (new)
 *   packetID       the packet's id (form-packets.PacketID)                    (2026-09-16)
 *   packetRun      random id minted once per packet link-open                 (2026-09-16)
 *   packetPatient  '1' when the link was issued for a specific patient        (2026-09-16)
 *
 * WHY packetID / packetRun / packetPatient EXIST (Action Center, 2026-09-16)
 *   The office gets ONE Action Center card per packet ("New Patient Packet · 3 of 7 forms"), not one
 *   per form. Each form's save sends these so forms-service → teamio-utility can group the submissions:
 *   a patient-specific link groups by patient (a resume days later updates the same card), a generic
 *   link groups by packetRun (one card per browser session). A link minted before this shipped has no
 *   packetID → resolvePacketProgress().packetID is '' → ViewForm sends no packet block → per-form cards,
 *   exactly the old behaviour.
 *
 * WHY packetSteps EXISTS
 *   Resume skips finished sections but positions must stay TRUE: a patient who already did 1, 2 and
 *   4 must see "Section 3", then "Section 5" — not "Section 1 of 5". packetNext alone cannot express
 *   that, so the parallel list of real positions rides along. Invariant:
 *   `packetSteps.length === packetNext.length`.
 *
 * FAIL-SOFT, NEVER FAIL-LOUD
 *   Any violation (non-numeric, step<1, step>total, remaining>total-step, list-length mismatch) hides
 *   the NUMBERS (step/total = null) but keeps `isPacket`, so the button override and the chain still
 *   work. A progress strip is a nicety; losing the "Save & Continue" button strands the patient.
 *   A link minted BEFORE this shipped carries only packetNext → isPacket true, no numbers, and its
 *   last hop has no packet keys at all → plain Submit, exactly today's behaviour.
 *
 * packetName is NEVER passed through systemTranslations' t(): its interpolation is String.replace,
 * which treats `$&` / `$1` in the replacement as capture-group syntax and mangles the name. Render it
 * as its own `{{ }}` node instead.
 *
 * Import-free on purpose: cypress/node-tests/forms-packet-progress.test.js loads and executes THIS
 * file (strip the trailing `export {...}` + `new Function`), so the tests verify production code
 * rather than a copy that can silently drift. Do not add an `import` or a `require` here.
 */

/**
 * A query param can arrive as an array (`?packetNext=a&packetNext=b`). Take the head.
 * @param {*} v
 * @returns {*}
 */
function firstValue(v) {
    if (Array.isArray(v)) return v.length > 0 ? v[0] : undefined;
    return v;
}

/**
 * Comma list → trimmed, blank-free array. Anything that is not a non-empty string → [].
 * @param {*} raw
 * @returns {string[]}
 */
function splitList(raw) {
    const s = firstValue(raw);
    if (typeof s !== 'string' || s.length === 0) return [];
    return s.split(',').map(part => part.trim()).filter(Boolean);
}

/** True when the value is present and not a blank string (an empty param is "absent"). */
function isPresent(raw) {
    const v = firstValue(raw);
    if (v === undefined || v === null) return false;
    return String(v).trim().length > 0;
}

/**
 * Strict non-negative integer parse. '3' → 3; '3.5', '3a', ' ', '-1', 'NaN' → null.
 * Deliberately strict: a garbled number must hide the strip, not render "Section NaN of 8".
 * @param {*} raw
 * @returns {number|null}
 */
function toInt(raw) {
    const v = firstValue(raw);
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    if (!/^\d+$/.test(s)) return null;
    return parseInt(s, 10);
}

/**
 * A short random id for one packet link-open. Uses Web Crypto when present (browsers, Node 19+),
 * Math.random otherwise — it only has to be unique per packet session, not secret.
 * @returns {string} 16 hex characters
 */
function mintPacketRun() {
    const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (c && typeof c.getRandomValues === 'function') {
        const bytes = new Uint8Array(8);
        c.getRandomValues(bytes);
        return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }
    let out = '';
    for (let i = 0; i < 16; i++) out += Math.floor(Math.random() * 16).toString(16);
    return out;
}

/**
 * Build the starting query for a packet link from the backend's formUrls.
 *
 * The chain is every entry whose `completed !== true`, in packet order. `completed` comes from
 * forms-service (a submission for this patient inside the resume window); it is ABSENT on generic
 * links and on responses from a backend that predates the feature — absent means "not done", so a
 * missing flag can only ever show MORE of the packet, never skip a section the patient has not filled.
 *
 * @param {Array<{formUrlID?: string, urlID?: string, completed?: boolean}>} formUrls
 * @param {string} [packetName]
 * @param {{packetID?: string, patientSpecific?: boolean}} [packetMeta] - grouping keys for the Action
 *        Center card; without a packetID none of packetID/packetRun/packetPatient are written.
 * @returns {{allComplete: boolean, first: Object|null, query: Object}}
 *          `query` holds STRINGS ready for $router.push; packetNext/packetSteps are omitted when the
 *          chain has a single form, packetName when it is blank.
 */
function buildPacketChain(formUrls, packetName, packetMeta) {
    const list = Array.isArray(formUrls) ? formUrls : [];
    const total = list.length;

    const chain = [];
    for (let i = 0; i < total; i++) {
        const entry = list[i];
        if (!entry || typeof entry !== 'object') continue;
        if (entry.completed === true) continue;
        chain.push({ entry: entry, step: i + 1 });
    }

    // No forms at all is a data problem, not a finished packet — never show "already completed".
    if (total === 0) return { allComplete: false, first: null, query: {} };

    if (chain.length === 0) return { allComplete: true, first: null, query: {} };

    const query = {};
    if (chain.length > 1) {
        query.packetNext = chain.slice(1).map(c => c.entry.urlID).join(',');
        query.packetSteps = chain.slice(1).map(c => String(c.step)).join(',');
    }
    query.packetTotal = String(total);
    query.packetStep = String(chain[0].step);

    const name = typeof packetName === 'string' ? packetName.trim() : '';
    if (name) query.packetName = name;

    const meta = packetMeta && typeof packetMeta === 'object' ? packetMeta : {};
    const packetID = meta.packetID === undefined || meta.packetID === null ? '' : String(meta.packetID).trim();
    if (packetID) {
        query.packetID = packetID;
        query.packetRun = mintPacketRun();
        if (meta.patientSpecific === true) query.packetPatient = '1';
    }

    return { allComplete: false, first: chain[0].entry, query: query };
}

/**
 * Read the packet state back out of a route query.
 *
 * @param {Object} query - `this.$route.query`
 * @returns {{isPacket: boolean, step: number|null, total: number|null, remaining: number,
 *            isLast: boolean, packetName: string, packetID: string, packetRun: string,
 *            patientSpecific: boolean}}
 */
function resolvePacketProgress(query) {
    const q = query && typeof query === 'object' ? query : {};

    const nextList = splitList(q.packetNext);
    const stepsList = splitList(q.packetSteps);
    const remaining = nextList.length;

    // isPacket is true for a legacy link (packetNext only) AND for a resumed packet whose last
    // section is the only one left (numbers, no packetNext).
    const hasNext = isPresent(q.packetNext);
    const hasSteps = isPresent(q.packetSteps);
    const hasNumbers = isPresent(q.packetStep) && isPresent(q.packetTotal);
    const isPacket = hasNext || hasNumbers;

    const nameRaw = firstValue(q.packetName);
    const packetName = typeof nameRaw === 'string' ? nameRaw : '';

    let step = toInt(q.packetStep);
    let total = toInt(q.packetTotal);

    const hideNumbers = () => { step = null; total = null; };

    if (step === null || total === null) {
        hideNumbers();
    } else if (step < 1 || step > total) {
        hideNumbers();
    } else if (remaining > total - step) {
        // More forms still to come than there are positions left — the chain and the numbers disagree.
        hideNumbers();
    } else if (hasNext && hasSteps && stepsList.length !== nextList.length) {
        hideNumbers();
    }

    const idRaw = firstValue(q.packetID);
    const runRaw = firstValue(q.packetRun);

    return {
        isPacket: isPacket,
        step: step,
        total: total,
        remaining: remaining,
        isLast: remaining === 0,
        packetName: packetName,
        packetID: typeof idRaw === 'string' ? idRaw.trim() : '',
        packetRun: typeof runRaw === 'string' ? runRaw.trim() : '',
        patientSpecific: String(firstValue(q.packetPatient)) === '1'
    };
}

/**
 * Pop the head of the chain and build the query for the NEXT form.
 *
 * The allowlist is EXPLICIT and short on purpose. The next form is resolved unscoped, so procedure
 * scope from a Get-Form QR code (aptNum / codes) and the Tx Presenter's txPlan must NOT ride along —
 * they belong to the form the patient just finished, and forwarding them would prefill the next form
 * with another form's context.
 *
 * @param {Object} query - the CURRENT form's `this.$route.query`
 * @param {string} [lang] - the language to carry forward (usually `this.$route.query.lang`)
 * @returns {{nextUrlID: string|null, query: Object}} nextUrlID is null when this was the last section.
 */
function nextPacketQuery(query, lang) {
    const q = query && typeof query === 'object' ? query : {};

    const nextList = splitList(q.packetNext);
    const stepsList = splitList(q.packetSteps);

    const nextUrlID = nextList.length > 0 ? nextList[0] : null;
    const restNext = nextList.slice(1);
    const nextStep = stepsList.length > 0 ? stepsList[0] : null;
    const restSteps = stepsList.slice(1);

    const out = {};
    if (restNext.length > 0) out.packetNext = restNext.join(',');
    if (restSteps.length > 0) out.packetSteps = restSteps.join(',');
    if (nextStep !== null) out.packetStep = nextStep;
    if (isPresent(q.packetTotal)) out.packetTotal = String(firstValue(q.packetTotal)).trim();

    const name = firstValue(q.packetName);
    if (typeof name === 'string' && name.length > 0) out.packetName = name;

    // Action Center grouping keys — forwarded verbatim so every section of this link-open reports
    // the same packet + run (see the header comment).
    ['packetID', 'packetRun', 'packetPatient'].forEach(key => {
        if (isPresent(q[key])) out[key] = String(firstValue(q[key])).trim();
    });

    out.packetVerified = '1';   // the patient cleared verification on the first section of this packet

    const l = firstValue(lang);
    if (l) out.lang = String(l);

    return { nextUrlID: nextUrlID, query: out };
}

export { buildPacketChain, resolvePacketProgress, nextPacketQuery };
